
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingComponent } from '@commonComponents/loading/loading.component';
import { CatalogIntegrationType } from '@interfaces/HttpClient/CatalogApiPublicModels';
import { CatalogIntegrationSettingsService } from '@services/catalog-integration-settings.service';
import { CatalogIntegrationService } from '@services/catalog-integration.service';
import { CatalogViewService } from '@services/catalog-view.service';
import { ReferrerService } from '@services/referrer.service';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-catalog-item-integration',
    templateUrl: './catalog-item-integration.component.html',
    styleUrls: ['./catalog-item-integration.component.scss'],
    providers: [CatalogViewService, CatalogIntegrationService],
    imports: [LoadingComponent]
})
export class CatalogItemIntegrationComponent implements OnInit {

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private catalogViewService: CatalogViewService,
		protected catalogIntegrationService: CatalogIntegrationService,
		private catalogIntegrationSettingsService: CatalogIntegrationSettingsService,
		private referrerService: ReferrerService
	) { }

	public isLoading = true;

	ngOnInit(): void {
		const documentReferrer = document.referrer
		this.catalogIntegrationSettingsService.isInIntegrationMode = true;

		combineLatest([this.route.params, this.route.queryParams]).subscribe(async ([params, queryParams]) => {
			this.isLoading = true;

			// Bei der Integration von ADE in Herstellerseiten mittels Iframe wird ein Redirect verwendet. Zum Beispiel wird die URL
			// https://www.ausschreiben.de/initintegration/v2/catalog/ade_next?integrationKey=0b591f54-0bb6-44ef-8bab-d94dc184c548 umgeleitet auf https://www.ausschreiben.de/catalog/ade_next, sofern
			// der IntegrationKey korrekt ist und folglich eine Berechtigung zur Einbettung besteht. Durch diesen internen Redirect ändert sich allerdings der document.referrer des Aufrufs,
			// der letztlich getrackt werden soll, zu www.ausschreiben.de. Da die Herstellerseite als Referrer mitgeliefert werden soll, wird vor dieser Umleitung der document.referrer
			// des Iframes - also die URL der Herstellerseite - in einen Service weggespeichert. Hierzu wird der document.referer beim initialen Laden der Seite in eine Varaiable weggespeichert, damit
			// Änderungen durch spätere Navigationen sich nicht auf ihn auswirken.
			// Zusätzlich kann es vorkommen, dass im Backend bereits redirects stattgefunden haben. In diesem Fall wird der urprüngliche refferer als Query-Parameter mitgeliefert. Sollte dieser Queryparameter gesetzt sein,
			// dann wird dieser statt des document referrer in den Service gespeichert.
			this.referrerService.set(queryParams.referrer ?? documentReferrer)

			// CatalogKey auf Basis der CatalogItemGuid bestimmen
			const catalogKey = (await this.catalogViewService.getAttachmentsByCatalogItemGuid(params.catalogItemGuid))?.catalogKey;

			// Validität des integrationKeys überprüfen. Nur falls dieser valide ist, wird ein Ergebnis zurückgeliefert.
			const isValid = await this.catalogIntegrationService.getCatalogIntegrationCatalogItemViewerSettings(catalogKey, queryParams.integrationKey)

			if (!isValid) {
				await this.router.navigate(["notFound"], { replaceUrl: true })
				this.isLoading = false
				return
			}

			// Settings aus der Datenbank holen und in Service speichern
			const settings = await this.catalogIntegrationService.getCatalogIntegrationCatalogItemViewerSettings(catalogKey, queryParams.integrationKey)
			this.catalogIntegrationSettingsService.settings = {
				catalogIntegrationType: queryParams.integrationType,
				canHighlightCatalogItem: false,
				canNavigateToStartingPage: true,
				showForeignLanguageCatalogs: false,
				showRelatedCatalogs: false,
				canSearch: false,
				collapseNavigation: false,
				neutralDesign: settings.neutralDesign
			}

			this.catalogIntegrationSettingsService.catalogIntegrationType = CatalogIntegrationType.CatalogItemViewer
			this.catalogIntegrationSettingsService.integrationKey = queryParams.integrationKey

			// Redirect zu URL ohne das Segment "integration" ausführen. Das Url-Segment "integration" dient nur als Trigger, um die diese Komponente aufzurufen und die Settings abzuholen
			await this.router.navigate(["catalog", catalogKey, "catalogItem", params.catalogItemGuid], { queryParams: { integrationKey: queryParams.integrationKey }, replaceUrl: true })

			this.isLoading = false
		})
	}
}
